import { useState } from 'react';
import { Button, Tooltip } from '@mantine/core';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { eksportXlsx, type Ustun } from '../lib/xlsx';

/**
 * Jadval ustidagi "XLSX" tugmasi — JORIY FILTR bo'yicha BUTUN ro'yxatni yuklaydi.
 *
 * NEGA SAHIFADAGINI EMAS. Jadval server tomonda sahifalangan: ekranda 20 qator
 * turadi, foydalanuvchi esa "jadvalni yuklab olish" deganda hammasini kutadi.
 * Faqat ko'rinib turgan 20 tasini bersak, buni FAYLNI OCHGANDA bilib olardi —
 * va bu jimgina noto'g'ri ma'lumot bilan ishlashga olib kelardi.
 *
 * FILTRLAR SAQLANADI: `params` — sahifadagi qidiruv/filtr/saralash o'sha holicha
 * uzatiladi. Ya'ni ekranda ko'rgan tanlov bilan fayldagi tanlov BIR XIL bo'ladi.
 *
 * Sahifalab olinadi (500 tadan): bitta so'rovda o'n minglab qator so'rash
 * serverni ham, brauzerni ham bloklardi.
 */

/** Bir so'rovda nechta qator. Serverdagi `limit` chegarasidan oshmasin. */
const BOLAK = 500;
/** Xavfsizlik to'xtatgichi — cheksiz sikl bo'lib qolmasin. */
const MAX_BOLAK = 200;

interface PagedResp<T> { items: T[]; total: number; pages: number }

export function TableExport<T>({
  endpoint,
  params = {},
  filename,
  ustunlar,
  total,
  sanaMaydoni,
  disabled,
}: {
  endpoint: string;
  params?: Record<string, string | number | undefined>;
  filename: string;
  ustunlar: Ustun<T>[];
  /** Ma'lum bo'lsa — tugmada qancha qator yuklanishi ko'rsatiladi. */
  total?: number;
  /** Sana maydoni — ko'p qatorda fayl choraklarga bo'linadi. */
  sanaMaydoni?: (row: T) => unknown;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [yuklanmoqda, setYuklanmoqda] = useState(false);

  const bajar = async () => {
    setYuklanmoqda(true);
    try {
      const hammasi: T[] = [];
      for (let sahifa = 1; sahifa <= MAX_BOLAK; sahifa++) {
        const r = await api.get<PagedResp<T> | T[]>(endpoint, {
          params: { ...params, page: sahifa, limit: BOLAK },
        });
        /* Endpointlarning bir qismi sahifalanmagan massiv qaytaradi
           (`envelope` faqat `page` berilganda o'raydi) — ikkala shaklni ham
           qabul qilamiz, aks holda ba'zi jadvallarda eksport jimgina bo'sh
           fayl berardi. */
        const d = r.data;
        const bolak = Array.isArray(d) ? d : d.items;
        hammasi.push(...bolak);
        const oxiri = Array.isArray(d) ? bolak.length < BOLAK : sahifa >= (d.pages || 1);
        if (oxiri || !bolak.length) break;
      }

      if (!hammasi.length) {
        notifications.show({ color: 'yellow', message: t('export.empty') });
        return;
      }

      const natija = await eksportXlsx({ filename, ustunlar, qatorlar: hammasi, sanaMaydoni });
      notifications.show({
        color: 'green',
        message: natija.varaqlar > 1
          ? t('export.doneSheets', { rows: natija.qatorlar, sheets: natija.varaqlar })
          : t('export.done', { rows: natija.qatorlar }),
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      });
    } finally {
      setYuklanmoqda(false);
    }
  };

  return (
    <Tooltip label={t('export.tooltip')} withArrow>
      <Button
        variant="default"
        leftSection={<IconFileSpreadsheet size={16} />}
        loading={yuklanmoqda}
        disabled={disabled || total === 0}
        onClick={bajar}
      >
        XLSX{total != null && total > 0 ? ` (${total})` : ''}
      </Button>
    </Tooltip>
  );
}
